import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { SubscriptionLinks, TrafficPoint, TrafficStats } from "./types";

type AuthState = "checking" | "locked" | "open";

const formatBytes = (value: number): string => {
  const units = ["B", "KB", "MB", "GB", "TB"];
  let amount = value;
  let unit = 0;
  while (Math.abs(amount) >= 1000 && unit < units.length - 1) {
    amount /= 1000;
    unit += 1;
  }
  const digits = unit >= 3 ? 2 : unit === 2 ? 1 : 0;
  return `${amount.toFixed(digits)} ${units[unit]}`;
};

const formatUptime = (seconds: number): string => {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return days > 0 ? `${days}D ${hours}H` : `${hours}H ${minutes}M`;
};

const formatTime = (iso: string): string =>
  new Intl.DateTimeFormat("zh-CN", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(new Date(iso));

const billedBytes = (value: { rx: number; tx: number }, mode: TrafficStats["quota"]["billing_mode"]): number => {
  if (mode === "bidirectional") return value.rx + value.tx;
  if (mode === "maximum") return Math.max(value.rx, value.tx);
  return value.tx;
};

const billingLabel = (mode: TrafficStats["quota"]["billing_mode"]): string => {
  if (mode === "bidirectional") return "BIDIRECTIONAL";
  if (mode === "maximum") return "MAX(IN, OUT)";
  return "OUTBOUND";
};

function BrandMark() {
  return (
    <div className="brand-mark" aria-label="2ander proxy telemetry">
      <span className="brand-index">02</span>
      <span className="brand-slash">/</span>
      <span>2ANDER</span>
    </div>
  );
}

function Login({ onSuccess }: { onSuccess: () => void }) {
  const [code, setCode] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const [seconds, setSeconds] = useState(30 - (Math.floor(Date.now() / 1000) % 30));
  const submittedCode = useRef("");

  useEffect(() => {
    const timer = window.setInterval(() => setSeconds(30 - (Math.floor(Date.now() / 1000) % 30)), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const submit = useCallback(
    async (nextCode: string) => {
      if (nextCode.length !== 6 || pending || submittedCode.current === nextCode) return;
      submittedCode.current = nextCode;
      setPending(true);
      setError("");
      try {
        const response = await fetch("/api/auth", {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code: nextCode }),
        });
        if (!response.ok) {
          const payload = (await response.json().catch(() => ({}))) as { error?: string };
          throw new Error(payload.error ?? "验证失败");
        }
        onSuccess();
      } catch (caught) {
        setError(caught instanceof Error ? caught.message : "验证失败");
        setCode("");
        submittedCode.current = "";
      } finally {
        setPending(false);
      }
    },
    [onSuccess, pending],
  );

  const handleChange = (value: string) => {
    const next = value.replace(/\D/gu, "").slice(0, 6);
    setCode(next);
    setError("");
    if (next.length === 6) void submit(next);
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    void submit(code);
  };

  return (
    <main className="login-shell">
      <div className="login-grid" aria-hidden="true" />
      <section className="login-panel">
        <div className="login-topline">
          <BrandMark />
          <span className="micro-status"><i /> PRIVATE NODE</span>
        </div>

        <div className="login-copy">
          <div className="section-code">AUTH / 01</div>
          <h1>输入动态验证码</h1>
          <p>打开验证器，输入 2ANDER PROXY 当前显示的六位数字。</p>
        </div>

        <form onSubmit={handleSubmit} className="otp-form">
          <label htmlFor="otp">TIME-BASED ONE-TIME PASSWORD</label>
          <div className={`otp-frame ${error ? "is-error" : ""}`}>
            <input
              id="otp"
              autoFocus
              autoComplete="one-time-code"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={code}
              onChange={(event) => handleChange(event.target.value)}
              aria-describedby="otp-hint"
              placeholder="000000"
            />
            <button type="submit" disabled={pending || code.length !== 6} aria-label="验证">
              {pending ? "···" : "↗"}
            </button>
          </div>
          <div id="otp-hint" className="otp-meta">
            <span className={error ? "error-text" : ""}>{error || "输入第六位后自动验证"}</span>
            <span>WINDOW {seconds.toString().padStart(2, "0")}S</span>
          </div>
        </form>

        <div className="login-footer">
          <span>LOS ANGELES / US</span>
          <span>DMIT PREMIUM EDGE</span>
          <span>SESSION 12H</span>
        </div>
      </section>
      <aside className="login-aside" aria-hidden="true">
        <div className="aside-number">1.5</div>
        <div className="aside-unit">TB / MONTH</div>
        <div className="aside-line" />
        <div className="aside-copy">PRIVATE INFRASTRUCTURE<br />TRAFFIC TELEMETRY</div>
      </aside>
    </main>
  );
}

function TrafficChart({ points, window }: { points: TrafficPoint[]; window: "five" | "hour" }) {
  const visible = points;
  const width = 880;
  const height = 250;
  const padX = 18;
  const padY = 20;
  const max = Math.max(...visible.map((day) => day.tx), 1);
  const plotted = visible.map((point, index) => {
    const x = visible.length === 1 ? width / 2 : padX + (index / (visible.length - 1)) * (width - padX * 2);
    const y = height - padY - (point.tx / max) * (height - padY * 2);
    return { ...point, x, y };
  });
  const polyline = plotted.map((point) => `${point.x},${point.y}`).join(" ");
  const area = plotted.length
    ? `${plotted[0].x},${height - padY} ${polyline} ${plotted.at(-1)!.x},${height - padY}`
    : "";

  const pointLabel = (timestamp: number) => new Intl.DateTimeFormat("zh-CN", {
    month: window === "hour" ? "2-digit" : undefined,
    day: window === "hour" ? "2-digit" : undefined,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "UTC",
  }).format(new Date(timestamp * 1000));

  return (
    <div className="chart-wrap">
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={window === "five" ? "最近一小时发出流量" : "最近二十四小时发出流量"}>
        <defs>
          <linearGradient id="traffic-fill" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stopColor="var(--acid)" stopOpacity=".22" />
            <stop offset="1" stopColor="var(--acid)" stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0.25, 0.5, 0.75, 1].map((ratio) => (
          <line key={ratio} x1="0" x2={width} y1={height * ratio} y2={height * ratio} className="chart-gridline" />
        ))}
        {area && <polygon points={area} fill="url(#traffic-fill)" />}
        {polyline && <polyline points={polyline} className="traffic-line" />}
        {plotted.map((point) => (
          <g key={point.timestamp}>
            <circle cx={point.x} cy={point.y} r="3" className="traffic-point" />
            <title>{`${pointLabel(point.timestamp)} · ${formatBytes(point.tx)}`}</title>
          </g>
        ))}
      </svg>
      <div className="chart-labels">
        {visible.map((point, index) => (
          <span key={point.timestamp}>{index % (window === "five" ? 2 : 4) === 0 || index === visible.length - 1 ? pointLabel(point.timestamp) : ""}</span>
        ))}
      </div>
    </div>
  );
}

function Metric({ index, label, value, note, accent }: { index: string; label: string; value: string; note: string; accent?: "orange" }) {
  return (
    <article className={`metric ${accent === "orange" ? "metric-orange" : ""}`}>
      <div className="metric-head"><span>{index}</span><span>{label}</span></div>
      <strong>{value}</strong>
      <small>{note}</small>
    </article>
  );
}

function ProtocolMetric({
  node,
  total,
}: {
  node: TrafficStats["protocol_accounting"]["nodes"][number];
  total: number;
}) {
  const share = total > 0 ? (node.billed / total) * 100 : 0;
  const healthy = node.status === "active" && node.accounting;
  return (
    <article className={`protocol-card protocol-${node.id}`}>
      <div className="protocol-card-head">
        <span>{node.protocol}</span>
        <b className={healthy ? "is-active" : "is-inactive"}>{healthy ? "ACTIVE" : "NO DATA"}</b>
      </div>
      <strong>{formatBytes(node.billed)}</strong>
      <div className="protocol-share"><span style={{ width: `${Math.min(share, 100)}%` }} /></div>
      <div className="protocol-meta">
        <span>SHARE {share.toFixed(1)}%</span>
        <span>{node.transport}</span>
      </div>
      <div className="protocol-directions">
        <span>OUT <b>{formatBytes(node.tx)}</b></span>
        <span>IN <b>{formatBytes(node.rx)}</b></span>
      </div>
    </article>
  );
}

function Dashboard({ onLogout }: { onLogout: () => void }) {
  const [stats, setStats] = useState<TrafficStats | null>(null);
  const [subscriptions, setSubscriptions] = useState<SubscriptionLinks | null>(null);
  const [error, setError] = useState("");
  const [subscriptionError, setSubscriptionError] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [chartWindow, setChartWindow] = useState<"five" | "hour">("five");
  const [copied, setCopied] = useState<keyof SubscriptionLinks | null>(null);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const [response, subscriptionResponse] = await Promise.all([
        fetch("/api/stats", { credentials: "same-origin", cache: "no-store" }),
        fetch("/api/subscriptions", { credentials: "same-origin", cache: "no-store" }),
      ]);
      if (response.status === 401 || subscriptionResponse.status === 401) {
        onLogout();
        return;
      }
      if (!response.ok) {
        const payload = (await response.json().catch(() => ({}))) as { error?: string };
        throw new Error(payload.error ?? "无法读取流量数据");
      }
      setStats((await response.json()) as TrafficStats);
      if (subscriptionResponse.ok) {
        setSubscriptions((await subscriptionResponse.json()) as SubscriptionLinks);
        setSubscriptionError("");
      } else {
        setSubscriptionError("订阅链接暂时不可用");
      }
      setError("");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "无法读取流量数据");
    } finally {
      setRefreshing(false);
    }
  }, [onLogout]);

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => void load(), 60_000);
    return () => window.clearInterval(timer);
  }, [load]);

  const days = useMemo(() => stats?.days.slice(-14).reverse() ?? [], [stats]);
  const risk = (stats?.quota.projected_percent ?? 0) >= 85;
  const chartPoints = useMemo(
    () => chartWindow === "five" ? stats?.five_minutes.slice(-12) ?? [] : stats?.hours.slice(-24) ?? [],
    [chartWindow, stats],
  );
  const quotaTicks = useMemo(
    () => [0, 0.25, 0.5, 0.75, 1].map((ratio) => formatBytes((stats?.quota.bytes ?? 0) * ratio)),
    [stats?.quota.bytes],
  );

  const copySubscription = async (kind: keyof SubscriptionLinks) => {
    const value = subscriptions?.[kind];
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(kind);
      window.setTimeout(() => setCopied((current) => current === kind ? null : current), 1800);
    } catch {
      setSubscriptionError("复制失败，请允许浏览器访问剪贴板后重试");
    }
  };

  const logout = async () => {
    await fetch("/api/logout", { method: "POST", credentials: "same-origin" }).catch(() => undefined);
    onLogout();
  };

  if (!stats) {
    return (
      <main className="loading-screen">
        <BrandMark />
        <div className="loading-line"><span /></div>
        <p>{error || "SYNCING TELEMETRY"}</p>
        {error && <button onClick={() => void load()}>RETRY</button>}
      </main>
    );
  }

  return (
    <main className="dashboard-shell">
      <header className="topbar">
        <BrandMark />
        <div className="topbar-node">
          <span className="node-light" />
          <span>{stats.node.label}</span>
          <b>ONLINE</b>
        </div>
        <div className="topbar-actions">
          <button className="text-button" onClick={() => void load()} disabled={refreshing}>{refreshing ? "SYNC" : "REFRESH"}</button>
          <button className="text-button danger-button" onClick={() => void logout()}>LOCK</button>
        </div>
      </header>

      <section className="hero-row">
        <div className="hero-copy">
          <span className="section-code">BANDWIDTH / PROVIDER ESTIMATE</span>
          <div className="hero-number">{formatBytes(stats.quota.used)}</div>
          <div className="hero-caption">APPROX. {billingLabel(stats.quota.billing_mode)} USED <span>//</span> {formatBytes(stats.quota.bytes)} ALLOCATION</div>
        </div>
        <div className="quota-dial" style={{ "--usage": `${Math.min(stats.quota.percent, 100)}%` } as React.CSSProperties}>
          <div>
            <strong>{stats.quota.percent.toFixed(2)}%</strong>
            <span>EST. USED</span>
          </div>
        </div>
      </section>

      <section className="quota-strip">
        <div className="quota-track"><span className={risk ? "risk" : ""} style={{ width: `${Math.min(stats.quota.percent, 100)}%` }} /></div>
        <div className="quota-labels">{quotaTicks.map((tick) => <span key={tick}>{tick}</span>)}</div>
        <div className="meter-disclaimer">
          ≈ {formatUptime(stats.quota.meter_lag_seconds)} PROVIDER LAG MODEL / PENDING {formatBytes(stats.quota.pending_sync)} / REALTIME {formatBytes(stats.quota.observed_used)}
        </div>
      </section>

      {error && <div className="system-alert"><span>ORIGIN WARNING</span>{error}</div>}

      <section className="metric-grid">
        <Metric index="01" label="TODAY EST." value={formatBytes(billedBytes(stats.quota.estimated_today, stats.quota.billing_mode))} note={`DMIT LAG MODEL / CUTOFF ${formatTime(stats.quota.meter_cutoff_at)}`} />
        <Metric index="02" label="REMAINING EST." value={formatBytes(stats.quota.remaining)} note={`PENDING SYNC ${formatBytes(stats.quota.pending_sync)}`} />
        <Metric index="03" label="PROJECTED" value={stats.quota.projected === null ? "WAITING" : formatBytes(stats.quota.projected)} note={`${stats.quota.state_label} / SAMPLE ${formatUptime(stats.quota.projection_observed_seconds)}`} accent={risk ? "orange" : undefined} />
        <Metric index="04" label="REALTIME OBSERVED" value={formatBytes(stats.quota.observed_used)} note={`RAW IN+OUT / COLLECTOR ${formatUptime(stats.collector_uptime_seconds)}`} />
      </section>

      <section className="protocol-section">
        <div className="panel-title">
          <div><span>05</span><h2>PROTOCOL OBSERVED</h2></div>
          <small>ACCOUNTED SINCE {formatTime(stats.protocol_accounting.accounted_since)}</small>
        </div>
        <div className="protocol-grid">
          {stats.protocol_accounting.nodes.map((node) => (
            <ProtocolMetric key={node.id} node={node} total={stats.protocol_accounting.total.billed} />
          ))}
        </div>
        <div className="protocol-note">
          PER-SERVICE IP ACCOUNTING / PROXY TRAFFIC ONLY / SSH, DASHBOARD AND SYSTEM TRAFFIC REMAIN IN THE VNSTAT TOTAL
        </div>
      </section>

      <section className="data-grid">
        <article className="data-panel chart-panel">
          <div className="panel-title">
            <div><span>06</span><h2>LIVE OUTBOUND</h2></div>
            <div className="chart-tabs" aria-label="流量时间窗口">
              <button className={chartWindow === "five" ? "active" : ""} onClick={() => setChartWindow("five")}>5 MIN</button>
              <button className={chartWindow === "hour" ? "active" : ""} onClick={() => setChartWindow("hour")}>24 H</button>
            </div>
          </div>
          {chartPoints.length > 0 ? <TrafficChart points={chartPoints} window={chartWindow} /> : <div className="chart-empty">WAITING FOR VNSTAT SAMPLES</div>}
        </article>

        <aside className="data-panel node-panel">
          <div className="panel-title"><div><span>07</span><h2>NODE STATE</h2></div></div>
          <dl>
            <div><dt>PUBLIC IP</dt><dd>{stats.node.public_ip}</dd></div>
            <div><dt>REGION</dt><dd>{stats.node.region}</dd></div>
            <div><dt>PROVIDER</dt><dd>{stats.node.provider}</dd></div>
            <div><dt>INTERFACE</dt><dd>{stats.interface}</dd></div>
            <div><dt>NEXT RESET</dt><dd>{formatTime(stats.quota.cycle_resets_at)}</dd></div>
            <div><dt>METER MODEL</dt><dd>≈ {formatUptime(stats.quota.meter_lag_seconds)} LAG</dd></div>
            <div><dt>PENDING SYNC</dt><dd>{formatBytes(stats.quota.pending_sync)}</dd></div>
            <div><dt>OBSERVED SINCE</dt><dd>{formatTime(stats.observed_since)}</dd></div>
            <div><dt>SERVER UPTIME</dt><dd>{formatUptime(stats.server_uptime_seconds)}</dd></div>
            <div><dt>LAST SYNC</dt><dd>{formatTime(stats.generated_at)}</dd></div>
          </dl>
          <div className="node-signal"><span /><span /><span /><span /><span /></div>
        </aside>
      </section>

      <section className="subscription-section">
        <div className="panel-title">
          <div><span>08</span><h2>DEVICE SUBSCRIPTIONS</h2></div>
          <small>VISIBLE AFTER OTP</small>
        </div>
        <div className="subscription-grid">
          <article className="subscription-card">
            <div><span>CLASH / MIHOMO</span><strong>完整规则订阅</strong></div>
            <p>适用于其他电脑的 Clash Verge，包含国内直连和海外代理规则。</p>
            <button disabled={!subscriptions} onClick={() => void copySubscription("clash")}>
              {copied === "clash" ? "已复制 ✓" : "复制 Clash 链接"}
            </button>
          </article>
          <article className="subscription-card subscription-card-orange">
            <div><span>SHADOWROCKET</span><strong>移动端订阅</strong></div>
            <p>在 Shadowrocket 的订阅设置中新增 URL，然后粘贴即可。</p>
            <button disabled={!subscriptions} onClick={() => void copySubscription("shadowrocket")}>
              {copied === "shadowrocket" ? "已复制 ✓" : "复制 Shadowrocket 链接"}
            </button>
          </article>
        </div>
        <div className={`subscription-note ${subscriptionError ? "is-error" : ""}`}>
          {subscriptionError || "订阅链接本身就是访问密钥；只放在自己的设备里，不要转发或截图。"}
        </div>
      </section>

      <section className="ledger-section">
        <div className="panel-title">
          <div><span>09</span><h2>TRAFFIC LEDGER</h2></div>
          <small>VNSTAT / {stats.interface.toUpperCase()}</small>
        </div>
        <div className="ledger">
          <div className="ledger-row ledger-head"><span>DATE</span><span>OUTBOUND</span><span>INBOUND</span><span>SHARE</span></div>
          {days.map((day) => {
            const dayBilled = billedBytes(day, stats.quota.billing_mode);
            const share = stats.quota.observed_used > 0 ? (dayBilled / stats.quota.observed_used) * 100 : 0;
            return (
              <div className="ledger-row" key={day.date}>
                <span>{day.date}</span><span>{formatBytes(day.tx)}</span><span>{formatBytes(day.rx)}</span><span>{share.toFixed(2)}%</span>
              </div>
            );
          })}
        </div>
      </section>

      <footer className="dashboard-footer">
        <span>2ANDER PRIVATE INFRASTRUCTURE</span>
        <span>VNSTAT STARTED {stats.observed_since.slice(0, 10)} / DMIT CONTROL PANEL REMAINS AUTHORITATIVE</span>
      </footer>
    </main>
  );
}

export default function App() {
  const [auth, setAuth] = useState<AuthState>("checking");

  const checkSession = useCallback(async () => {
    try {
      const response = await fetch("/api/session", { credentials: "same-origin", cache: "no-store" });
      const payload = (await response.json()) as { authenticated?: boolean };
      setAuth(payload.authenticated ? "open" : "locked");
    } catch {
      setAuth("locked");
    }
  }, []);

  useEffect(() => { void checkSession(); }, [checkSession]);

  if (auth === "checking") return <main className="loading-screen"><BrandMark /><div className="loading-line"><span /></div><p>CHECKING SESSION</p></main>;
  if (auth === "locked") return <Login onSuccess={() => setAuth("open")} />;
  return <Dashboard onLogout={() => setAuth("locked")} />;
}
